const requiredChecks = ["terminalIdentity", "windows", "spooler", "printers", "printJobs", "ports", "logSignals"];
const repairReview = ["blocked", "failed", "manual_review", "needs_physical_confirmation"];

export function terminalSummary(item) {
    const findings = item.findings || [];
    const jam = findings.find((finding) => finding.id === "PRINTER_JAM");
    const first = jam || findings.find((finding) => finding.severity === "high") || findings[0];
    if (jam || item.physicalPrinterStatus === "unresolved_jam") return {
        tone: jam?.currentSignal === true ? "danger" : "attention", title: jam?.currentSignal === true ? "Принтер сообщает о замятии" : "Нужно перепроверить прежний сигнал принтера",
        description: jam?.currentSignal === true ? "Получен свежий сигнал ошибки. Он требует проверки показаний принтера и выдачи бумаги." : "Сигнал сохранён в истории. Сам по себе он не подтверждает, что бумага сейчас застряла.",
        action: "Hermes проверит текущий статус принтера через ПО. Физический выход пробного документа подтверждает сотрудник у терминала.",
    };
    if (first) return {
        tone: first.severity === "high" ? "danger" : "attention", title: first.title,
        description: `Найдено проблем и наблюдений: ${findings.length}. Ниже — инструкция для проверки и устранения.`,
        action: first.steps?.[0]?.instruction || "Откройте подробности проблемы ниже.",
    };
    if (item.expectedOffline && !item.collectionBlocked && !repairReview.includes(item.remediation?.status)) return {
        tone: "neutral", title: item.businessHours?.state === 'opening' ? 'Ожидаем подключения после открытия' : 'Салон закрыт по графику',
        description: "Отсутствие связи вне рабочего времени допустимо и само по себе не означает поломку терминала.",
        action: "Hermes проверит подключение после открытия. Это не подтверждает простой и не разрешает повтор денежных операций.",
    };
    if (repairReview.includes(item.remediation?.status)) return {
        tone: "attention", title: item.remediation.status === "needs_physical_confirmation" ? "Нужно проверить результат на месте" : "Нужна проверка специалиста",
        description: "Автоматический сценарий требует участия техподдержки.",
        action: item.remediation.status === "needs_physical_confirmation" ? "Подтвердите физическую выдачу бумаги и отдельно проверьте приём купюры." : "Проверьте результат предыдущего исправления перед повторными действиями.",
    };
    if (item.remediation?.status === "running") return {
        tone: "neutral", title: "Выполняется автоисправление",
        description: "Терминал выполняет проверенный сценарий обслуживания.",
        action: "Дождитесь окончания сценария. После него появится результат и следующий шаг.",
    };
    if (item.online === false) return {
        tone: "attention", title: "Нет связи с терминалом",
        description: "Свежую проверку выполнить не удалось. Причина отсутствия связи ещё не установлена.",
        action: "Уточните, включён ли терминал и есть ли интернет в салоне. После восстановления связи повторите проверку.",
    };
    if (item.error) return {
        tone: "attention", title: "Не удалось проверить терминал",
        description: "Достоверные данные последней проверки не получены.",
        action: item.error === "MESH_TIMEOUT" ? "Специалисту нужно проверить завершение предыдущей команды на терминале перед повторным запуском." : "Проверьте подключение и причину сбоя в подробностях ниже.",
    };
    if (item.stale || item.evidenceReview?.stale) return {
        tone: "attention", title: "Нужна свежая проверка",
        description: "Сохранённые данные устарели и могут отличаться от текущего состояния.",
        action: "Нажмите «Проверить сейчас» и дождитесь нового результата.",
    };
    if (!item.report) return {
        tone: "neutral", title: "Терминал ожидает проверки",
        description: "Агент подключён, результаты диагностики ещё не получены.",
        action: "Запустите проверку или дождитесь автоматического сбора.",
    };
    if (item.observations?.length) return {
        tone: 'neutral', title: 'Есть наблюдения для проверки',
        description: 'Подтверждённая текущая поломка этими данными не установлена. Есть наблюдения из замера ресурсов, настроек или журнала.',
        action: 'Hermes сопоставит наблюдения с текущей работой ПО. Они показаны отдельно от подтверждённых проблем.',
    };
    // The legacy agent may lack its optional installation marker. The collector
    // separately verifies live terminal identity; marker absence is not a failed Windows check.
    if (item.report.errors?.some((error) => error.section !== "supportIdentity") || requiredChecks.some((check) => !item.report.coverage?.includes(check))) return {
        tone: "attention", title: "Проверка выполнена частично",
        description: "В полученных данных ошибок не найдено, но часть проверок недоступна.",
        action: "Откройте подробности: там указано, какие данные не удалось получить.",
    };
    return {
        tone: "clear", title: "Удалённая проверка: ошибок не найдено",
        description: "Проверены ПО, устройства и очередь печати в Windows, а также свежие ошибки журнала.",
        action: "По этой проверке удалённого вмешательства не требуется. Следующая проверка запустится автоматически.",
    };
}

export function equipmentChecks(item) {
    const report = item.report || {};
    const config = report.equipment || {};
    const covered = (section) => report.coverage?.includes(section) && !report.errors?.some((error) => error.section === section);
    const port = (value) => String(value || "").replace(/:$/, "").toUpperCase();
    const printer = (report.printers || []).find((row) => row.Name === config.receiptPrinterName);
    const printerError = printer && (printer.WorkOffline === true || printer.PrinterStatus === 7 || Number(printer.DetectedErrorState) > 2);
    const jamFinding = (item.findings || []).find((finding) => finding.id === "PRINTER_JAM");
    const jam = item.physicalPrinterStatus === "unresolved_jam" || !!jamFinding;
    const currentJam = jamFinding?.currentSignal === true;
    const printerCheck = {
        id: "printer", icon: "printer", title: "Чековый принтер", tone: jam || printerError ? "attention" : "neutral",
        value: jam ? currentJam ? "Есть свежий сигнал замятия" : "Прежний сигнал требует проверки" : !covered("printers") ? "Нет данных Windows" : printerError ? "Windows сообщает об ошибке" : printer ? "Windows не сообщает об ошибках" : "Выбранный принтер не найден",
        detail: printer ? `${printer.Name} · ${printer.PortName || "порт не указан"}` : config.receiptPrinterName || "Настройки принтера не получены",
        note: jam ? "Статус устройства и выход бумаги проверяются отдельно" : "Выход бумаги проверяется на месте",
    };
    const connection = report.printerConnection;
    if (!jam && connection?.route === 'kkm') {
        printerCheck.value = 'Печать через кассу';
        printerCheck.detail = [connection.driver === 'atolkkmcom' ? 'АТОЛ COM' : connection.driver, connection.port, connection.port2].filter(Boolean).join(' · ') || 'По настройкам ПО';
        printerCheck.tone = 'neutral';
    } else if (!jam && connection?.route === 'direct' && connection.transport === 'serial') {
        printerCheck.value = 'Печать напрямую через COM';
        printerCheck.detail = [connection.port, connection.baudRate ? connection.baudRate + ' бод' : null].filter(Boolean).join(' · ');
        printerCheck.tone = 'neutral';
    }
    const jobs = covered("printJobs") ? (report.printJobs || []).length : null;
    const queueCheck = {
        id: "queue", icon: "list", title: "Очередь печати", tone: jobs ? "attention" : "neutral",
        value: jobs === null ? "Нет данных" : jobs === 0 ? "Заданий нет" : `Заданий в Windows: ${jobs}`,
        detail: jobs ? "Требуется проверить состояние заданий" : jobs === 0 ? "В снимке Windows очередь пуста" : "Очередь не удалось проверить",
        note: "По последнему снимку",
    };
    if (connection?.route === 'kkm' || connection?.route === 'direct' && connection.transport === 'serial') {
        queueCheck.title = 'Очередь Windows';
        queueCheck.detail = 'Этот маршрут печати не использует очередь Windows';
        queueCheck.tone = 'neutral';
    }
    const billPort = config.useCoinBill === true ? config.CoinBillPort : config.BillPort;
    const cashEnabled = config.useBill === true || config.useCoinBill === true;
    const found = covered("physicalPorts") && (report.physicalPorts || []).some((row) => port(row.port) === port(billPort));
    const billCheck = {
        id: "bill", icon: "wallet", title: "Купюроприёмник", tone: cashEnabled && covered("physicalPorts") && !found ? "attention" : "neutral",
        value: !report.equipment || !covered("equipment") ? "Настройки не получены" : !cashEnabled ? "Приём наличных выключен" : !billPort ? "Порт не указан" : !covered("physicalPorts") ? "Порт не проверен" : found ? `${billPort} найден в Windows` : `${billPort} отсутствует в Windows`,
        detail: cashEnabled ? "Проверено наличие порта" : "По настройкам терминального ПО",
        note: "Приём купюр не проверялся",
    };
    return [printerCheck, queueCheck, billCheck];
}
